import { useState } from 'react';

import { Alert, Box, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
  CONTENT_LABELS,
  CONTENT_ORDER,
  type BgtHistoryDetail,
  getResidentBgtHistory,
} from '@/lib/api/bgt-history';
import type { ResidentDto } from '@/lib/api/residents';
import { PROGRAM_LABEL } from '@/lib/program-labels';

import { ExportMenu } from '@/components/export/ExportMenu';

import { BgtHistoryChart } from '../BgtHistoryChart';
import { HistorySummaryCard } from '../HistorySummaryCard';
import { LevelCards } from '../LevelCards';
import { type PeriodRange, PeriodPickerBar } from '../PeriodPickerBar';

interface Props {
  resident: ResidentDto | null;
}

function defaultRange(): PeriodRange {
  const to = dayjs().startOf('day');
  const from = to.subtract(13, 'day'); // 2주
  return { from, to };
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '0분';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min}분`;
  const h = Math.floor(min / 60);
  const remM = min % 60;
  return remM > 0 ? `${h}시간 ${remM}분` : `${h}시간`;
}

function DailyTable({ data }: { data: BgtHistoryDetail }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
          일자별 ({data.days.length}일)
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.85rem',
              '& th, & td': {
                p: 0.75,
                borderBottom: 1,
                borderColor: 'divider',
                textAlign: 'right',
                whiteSpace: 'nowrap',
              },
              '& th:first-of-type, & td:first-of-type': { textAlign: 'left' },
              '& th': { fontWeight: 700, bgcolor: 'background.default' },
              '& tr.empty td': { color: 'text.disabled' },
            }}
          >
            <Box component="thead">
              <Box component="tr">
                <Box component="th">날짜</Box>
                <Box component="th">평균</Box>
                <Box component="th">문제</Box>
                <Box component="th">시간</Box>
                {CONTENT_ORDER.map((t) => (
                  <Box component="th" key={t}>
                    {CONTENT_LABELS[t]}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {data.days.map((d) => (
                <Box component="tr" key={d.date} className={d.hasData ? '' : 'empty'}>
                  <Box component="td">{d.date}</Box>
                  <Box component="td">{d.avgScore === null ? '—' : `${d.avgScore}점`}</Box>
                  <Box component="td">{d.hasData ? d.testCount : '—'}</Box>
                  <Box component="td">{d.hasData ? formatDuration(d.totalUseTime) : '—'}</Box>
                  {CONTENT_ORDER.map((t) => {
                    const v = d.contentScores[t];
                    return (
                      <Box component="td" key={t}>
                        {v === undefined ? '—' : `${v}점`}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * 의뢰서 5-2 탭2 BGT 기간별 데이터 (P5-2).
 *
 * 본 단계: 기간 선택 UI + 응답 데이터를 표로 표시 + 요약 카드.
 * 후속:
 *   - P5-3 콘텐츠별 꺾은선 그래프
 *   - P5-4 난이도 등급 추이
 *   - P5-5 기간 데이터 내보내기 (P0-10 공통 모듈)
 */
export function TabPeriod({ resident }: Props) {
  const [range, setRange] = useState<PeriodRange>(defaultRange);

  const from = range.from.format('YYYY-MM-DD');
  const to = range.to.format('YYYY-MM-DD');

  const { data, isLoading, isError, error } = useQuery<BgtHistoryDetail>({
    queryKey: ['residents', 'bgt-history', resident?.id, from, to],
    queryFn: () => getResidentBgtHistory(resident!.id, from, to),
    enabled: !!resident,
    staleTime: 60 * 1000,
  });

  if (!resident) return null;

  const exportFallbackName = `${PROGRAM_LABEL.bgt.short}기간데이터_${from}_${to}`;

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              기간 선택
            </Typography>
            <ExportMenu
              baseUrl={`/residents/${resident.id}/bgt/history/export`}
              params={{ from, to }}
              fallbackBaseName={exportFallbackName}
              disabled={!data || isLoading}
            />
          </Stack>
          <PeriodPickerBar value={range} onChange={setRange} />
        </CardContent>
      </Card>

      {isError && (
        <Alert severity="error">
          {(error as Error)?.message ?? '기간 데이터를 불러오지 못했습니다'}
        </Alert>
      )}

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : data ? (
        <>
          <HistorySummaryCard
            title="구간 요약"
            summary={data.summary}
            from={data.from}
            to={data.to}
          />
          <BgtHistoryChart days={data.days} />
          <LevelCards levels={data.currentLevels} />
          <DailyTable data={data} />
        </>
      ) : null}
    </Stack>
  );
}
